import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { BellRing, Mail, MessageSquare, Smartphone, MonitorSmartphone, CheckCheck, ShieldAlert, RefreshCw } from "lucide-react";

const TIPO_LABELS = {
  cobro_vencido: "Cobro vencido", duplicado_detectado: "Duplicado detectado",
  margen_excedido: "Margen excedido", cobro_registrado: "Cobro registrado",
  pago_registrado: "Pago registrado", load_desbloqueado: "Load desbloqueado",
  prueba: "Alerta de prueba", resumen_diario: "Resumen diario",
};
const SEVERIDAD_CLS = {
  info: "bg-slate-100 text-slate-700 border border-slate-200",
  media: "bg-amber-100 text-amber-700 border border-amber-200",
  alta: "bg-orange-100 text-orange-700 border border-orange-200",
  critica: "bg-red-100 text-red-700 border border-red-200",
};
const CANAL_ICON = { email: Mail, sms: Smartphone, whatsapp: MessageSquare, inapp: MonitorSmartphone };

export default function Alerts() {
  const { user } = useAuth();
  const [data, setData] = useState({ items: [], total: 0 });
  const [tipo, setTipo] = useState("");
  const [severidad, setSeveridad] = useState("");
  const [leida, setLeida] = useState("");
  const [selected, setSelected] = useState([]);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (tipo) params.tipo = tipo;
      if (severidad) params.severidad = severidad;
      if (leida) params.leida = leida;
      const { data: res } = await api.get("/alerts", { params });
      setData(res);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [tipo, severidad, leida]);

  useEffect(() => { load(); }, [load]);

  const loadPreview = useCallback(async () => {
    if (user?.rol !== "dueno") return;
    setPreviewLoading(true);
    try {
      const { data: res } = await api.get("/alert-settings/preview-envio");
      setPreview(res);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setPreviewLoading(false);
    }
  }, [user]);

  useEffect(() => { loadPreview(); }, [loadPreview]);

  const markRead = async (id) => {
    await api.post(`/alerts/${id}/mark-read`);
    load();
  };

  const markSelectedRead = async () => {
    if (!selected.length) return;
    await api.post("/alerts/mark-read-bulk", selected);
    setSelected([]);
    toast.success("Alertas marcadas como leídas");
    load();
  };

  const toggleSelect = (id) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  return (
    <div>
      <PageHeader
        title="Alertas"
        subtitle="Todas las alertas generadas por el sistema — cobros vencidos, duplicados, márgenes, cobros y pagos"
        actions={
          selected.length > 0 && (
            <Button onClick={markSelectedRead} className="gap-2" data-testid="alerts-mark-selected-read">
              <CheckCheck className="h-4 w-4" /> Marcar {selected.length} como leída(s)
            </Button>
          )
        }
      />

      {user?.rol === "dueno" && (
        <Card className="mb-4 border-amber-200 bg-amber-50/40" data-testid="alerts-preview-envio-panel">
          <CardHeader className="flex flex-row items-center justify-between gap-2 py-3">
            <CardTitle className="flex items-center gap-2 text-sm font-heading">
              <ShieldAlert className="h-4 w-4 text-amber-600" /> Antes de encender el correo real
            </CardTitle>
            <Button size="sm" variant="ghost" onClick={loadPreview} disabled={previewLoading} data-testid="alerts-preview-refresh-btn" className="gap-1">
              <RefreshCw className={`h-3.5 w-3.5 ${previewLoading ? "animate-spin" : ""}`} /> Actualizar
            </Button>
          </CardHeader>
          <CardContent className="pb-4 text-sm">
            {!preview && previewLoading && <p className="text-xs text-muted-foreground">Calculando vista previa…</p>}
            {preview && (
              <div className="space-y-2" data-testid="alerts-preview-envio-content">
                <p>
                  Modo simulación de correo:{" "}
                  <span className={preview.simulacion_activa ? "font-semibold text-slate-700" : "font-semibold text-red-700"}>
                    {preview.simulacion_activa ? "ENCENDIDA (no se envía nada real)" : "APAGADA (se enviaría correo real)"}
                  </span>
                </p>
                <p>
                  Estado de la API key de Resend:{" "}
                  <span className={preview.api_key_ok ? "font-semibold text-green-700" : "font-semibold text-red-700"} data-testid="alerts-preview-api-key-status">
                    {preview.api_key_ok ? "Válida" : "No válida o no configurada"}
                  </span>
                </p>
                {preview.api_key_detalle && (
                  <p className="font-medium text-red-700" data-testid="alerts-preview-api-key-error">{preview.api_key_detalle}</p>
                )}
                <p>
                  Remitente configurado: <span className="font-mono text-xs">{preview.from_name || "—"} &lt;{preview.from_email || "—"}&gt;</span>
                </p>
                <p>
                  Hay <b>{preview.loads_vencidos}</b> load(s) vencido(s) ahora mismo, con <b>{preview.destinatarios_total}</b> destinatario(s) suscripto(s) a cobro vencido por correo.
                </p>
                <p>
                  Si se enciende el correo real, la próxima corrida enviaría aproximadamente{" "}
                  <b>{preview.emails_individuales_estimados}</b> correo(s) individuales
                  {preview.emails_consolidados_estimados > 0 && (
                    <> + <b>{preview.emails_consolidados_estimados}</b> correo(s) consolidado(s) por el excedente</>
                  )}{" "}
                  (tope actual: {preview.emails_max_por_corrida} por corrida).
                </p>
                {preview.destinatarios_email?.length > 0 && (
                  <p className="text-xs text-muted-foreground">
                    Destinatarios: {preview.destinatarios_email.map((d) => `${d.nombre} <${d.email}>`).join(", ")}
                  </p>
                )}
                {preview.muestra?.length > 0 && (
                  <div className="mt-2 rounded border border-dashed border-amber-300 bg-white p-2">
                    <p className="mb-1 text-xs font-semibold text-muted-foreground">Muestra de loads que dispararían aviso:</p>
                    <ul className="space-y-0.5 text-xs">
                      {preview.muestra.map((m, i) => (
                        <li key={i} data-testid={`alerts-preview-muestra-${i}`}>
                          {m.numero_load} — {m.cliente || "sin cliente"} — ${m.monto} {m.moneda} — {m.dias} día(s) vencido
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <div className="mb-4 flex flex-wrap gap-3">
        <Select value={tipo || "todos"} onValueChange={(v) => setTipo(v === "todos" ? "" : v)}>
          <SelectTrigger className="w-52" data-testid="alerts-filter-tipo"><SelectValue placeholder="Tipo" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos los tipos</SelectItem>
            {Object.entries(TIPO_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={severidad || "todas"} onValueChange={(v) => setSeveridad(v === "todas" ? "" : v)}>
          <SelectTrigger className="w-40" data-testid="alerts-filter-severidad"><SelectValue placeholder="Severidad" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todas">Todas las severidades</SelectItem>
            <SelectItem value="info">Info</SelectItem>
            <SelectItem value="media">Media</SelectItem>
            <SelectItem value="alta">Alta</SelectItem>
            <SelectItem value="critica">Crítica</SelectItem>
          </SelectContent>
        </Select>
        <Select value={leida || "todas"} onValueChange={(v) => setLeida(v === "todas" ? "" : v)}>
          <SelectTrigger className="w-40" data-testid="alerts-filter-leida"><SelectValue placeholder="Estado" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todas">Leídas y no leídas</SelectItem>
            <SelectItem value="false">No leídas</SelectItem>
            <SelectItem value="true">Leídas</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-md border border-border bg-white" data-testid="alerts-table-container">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-8" />
              <TableHead>Tipo</TableHead>
              <TableHead>Severidad</TableHead>
              <TableHead>Load</TableHead>
              <TableHead>Mensaje</TableHead>
              <TableHead>Canales</TableHead>
              <TableHead>Fecha</TableHead>
              <TableHead className="text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {!loading && data.items.length === 0 && (
              <TableRow><TableCell colSpan={8} className="py-10 text-center text-sm text-muted-foreground" data-testid="alerts-empty-state">
                No hay alertas con estos filtros.
              </TableCell></TableRow>
            )}
            {data.items.map((a, i) => (
              <TableRow key={a.id} data-testid={`alerts-row-${i}`} className={!a.leida ? "bg-green-50/40" : ""}>
                <TableCell><Checkbox checked={selected.includes(a.id)} onCheckedChange={() => toggleSelect(a.id)} data-testid={`alerts-select-${i}`} /></TableCell>
                <TableCell className="text-sm">
                  <div className="flex items-center gap-2">
                    {!a.leida && <BellRing className="h-3.5 w-3.5 text-primary" />}
                    {TIPO_LABELS[a.tipo] || a.tipo}
                  </div>
                </TableCell>
                <TableCell><Badge className={SEVERIDAD_CLS[a.severidad]}>{a.severidad}</Badge></TableCell>
                <TableCell className="font-mono text-xs">{a.load_numero || "-"}</TableCell>
                <TableCell className="max-w-md truncate text-sm" title={a.mensaje}>{a.mensaje}</TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    {(a.envios || []).map((e, j) => {
                      const Icon = CANAL_ICON[e.canal] || Mail;
                      return (
                        <span key={j} title={`${e.canal}: ${e.estado}`}
                          className={`flex h-5 w-5 items-center justify-center rounded ${e.simulada ? "bg-slate-100 text-slate-500" : e.estado === "enviada" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                          <Icon className="h-3 w-3" />
                        </span>
                      );
                    })}
                    {(!a.envios || a.envios.length === 0) && <span className="text-xs text-muted-foreground">—</span>}
                  </div>
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">{a.created_at?.slice(0, 16).replace("T", " ")}</TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="ghost" onClick={() => setDetail(a)} data-testid={`alerts-view-${i}`}>Ver</Button>
                  {!a.leida && (
                    <Button size="sm" variant="ghost" onClick={() => markRead(a.id)} data-testid={`alerts-mark-read-${i}`}>Marcar leída</Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="max-w-2xl" data-testid="alerts-detail-modal">
          <DialogHeader><DialogTitle className="font-heading">{detail && (TIPO_LABELS[detail.tipo] || detail.tipo)}</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm">
              <p><span className="font-medium text-muted-foreground">Mensaje:</span> {detail.mensaje}</p>
              {detail.load_numero && <p><span className="font-medium text-muted-foreground">Load:</span> {detail.load_numero}</p>}
              <div className="space-y-3">
                {(detail.envios || []).map((e, j) => (
                  <div key={j} className="rounded-md border border-border p-3">
                    <div className="mb-1 flex items-center justify-between">
                      <span className="text-xs font-semibold uppercase text-muted-foreground">{e.canal} — {e.destinatario}</span>
                      <Badge className={e.simulada ? "bg-slate-100 text-slate-600" : e.estado === "enviada" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}>
                        {e.simulada ? "SIMULADA" : e.estado}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">Remitente: {e.remitente || "—"}</p>
                    {e.asunto && <p className="text-xs text-muted-foreground">Asunto: {e.asunto}</p>}
                    {e.cuerpo == null ? (
                      <p className="mt-2 rounded border border-dashed border-border bg-slate-50 p-2 text-xs italic text-muted-foreground" data-testid="alert-cuerpo-restringido">
                        Contenido no disponible para tu rol.
                      </p>
                    ) : e.canal === "email" ? (
                      <div className="mt-2 max-h-52 overflow-auto rounded border border-dashed border-border bg-slate-50 p-2 text-xs"
                        dangerouslySetInnerHTML={{ __html: e.cuerpo }} />
                    ) : (
                      <p className="mt-2 rounded border border-dashed border-border bg-slate-50 p-2 text-xs">{e.cuerpo}</p>
                    )}
                    {e.detalle_envio && <p className="mt-1 text-xs text-muted-foreground">Detalle: {e.detalle_envio}</p>}
                  </div>
                ))}
                {(!detail.envios || detail.envios.length === 0) && (
                  <p className="text-xs text-muted-foreground">Esta alerta no tuvo canales externos activos con destinatarios configurados (solo quedó registrada en el sistema).</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
